import type { Lead, HandoverContact } from '@/shared/types'

// Build an ISO timestamp for a date + HH:MM in the BDR's local timezone, so
// the server stores the wall-clock moment the BDR meant ("10:00 in Stockholm"
// rather than naive UTC). Used by the callback-alert flow when a specific
// time is committed; otherwise the date-only ISO string suffices.
export function buildLocalIso(yyyyMmDd: string, hhMm: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map(Number)
  const [hh, mm] = hhMm.split(':').map(Number)
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return yyyyMmDd
  const local = new Date(y, m - 1, d, hh, mm, 0, 0)
  return local.toISOString()
}

// Swap a contact into the primary slot client-side so the star click flips the
// visible card instantly instead of waiting for the PATCH+refetch to land. The
// old primary moves into the decision-maker list at the new primary's prior
// slot. No-ops if the target isn't found or is already primary. The server's
// canonical handover (with full id / edited / discovery metadata) replaces
// this projection when the PATCH response splices into the cache.
export function swapPrimaryByKey(lead: Lead, newPrimaryKey: string): Lead {
  if (!lead.handover) return lead
  const matches = (c: HandoverContact): boolean => {
    if (c.id && c.id === newPrimaryKey) return true
    const email = (c.email || '').trim().toLowerCase()
    if (email && email === newPrimaryKey) return true
    const name = (c.name || '').trim().toLowerCase()
    return !!name && name === newPrimaryKey
  }
  const { primary, decisionMakers } = lead.handover
  if (matches(primary)) return lead
  const idx = decisionMakers.findIndex(matches)
  if (idx < 0) return lead
  const newPrimary = decisionMakers[idx]
  const newDecisionMakers = [
    ...decisionMakers.slice(0, idx),
    primary,
    ...decisionMakers.slice(idx + 1),
  ]
  return {
    ...lead,
    primaryContactOverride: newPrimaryKey,
    handover: {
      ...lead.handover,
      primary: newPrimary,
      decisionMakers: newDecisionMakers,
    },
  }
}
